import {
  APP_USER_RECORDING_ROLE,
  RECORDING_PEMISSION_ACTIONS,
  RECORDING_PERMISSIONS,
  RecordingScope,
} from '@nuso/iam/types';

export const RECORDING_ROLE_LEVEL_MAPPING = {
  USER: ['own'],
  SUPERVISOR: ['own', 'group'],
  ADMINISTRATOR: ['own', 'group', 'all'],
  OWNER: ['own', 'group', 'all'],
}

export function recordingRoleMapping(role: APP_USER_RECORDING_ROLE) {
  switch (role) {
    case 'ADMINISTRATOR':
      return 'Supervisor+';
    case 'SUPERVISOR':
      return 'Supervisor';
    case 'USER':
      return 'User';
    case 'OWNER':
      return 'Owner';
    default:
      return role;
  }
}

export function countOfRecordingPermissions(permissions: string[]): number {
  return permissions.filter(t => Object.values(RECORDING_PERMISSIONS).includes(t)).length;
}

export function recordingRoleFromPermissions(permissions: string[]): APP_USER_RECORDING_ROLE {
  // Leaving OWNER / ADMINISTRATOR_FULL out for now per nigels request
  // if (hasAllRecordingPermissionsForScope(permissions, 'recording.all')) {
  //   return 'OWNER';
  // }
  if (hasAnyRecordingPermissionsForScope(permissions, 'recording.all')) {
    return 'ADMINISTRATOR';
  } else if (hasAnyRecordingPermissionsForScope(permissions, 'recording.group')) {
    return 'SUPERVISOR';
  } else {
    return 'USER';
  }
}

export function hasFullRecordingPermissionsForRole(permissions: string[], role: APP_USER_RECORDING_ROLE) {
  switch (role) {
    case 'USER':
      return hasAllRecordingPermissionsForScope(permissions, 'recording.own');
    case 'SUPERVISOR':
      return hasAllRecordingPermissionsForScope(permissions, 'recording.own')
          && hasAllRecordingPermissionsForScope(permissions, 'recording.group');
    // For admin and owner, just make sure they have what they need for the others...
    case 'ADMINISTRATOR':
    case 'OWNER':
      return hasAllRecordingPermissionsForScope(permissions, 'recording.own')
          && hasAllRecordingPermissionsForScope(permissions, 'recording.group');
  }
}

export function fullRecordingingPermissionsForRole(role: APP_USER_RECORDING_ROLE) {
  if ( role === 'OWNER') {
    return allPermissionsForScope('recording.own')
      .concat(allPermissionsForScope('recording.group'))
      .concat(allPermissionsForScope('recording.all'));
  } else if (role === 'ADMINISTRATOR') {
    return allPermissionsForScope('recording.own')
      .concat(allPermissionsForScope('recording.group'))
      .concat(['recording.all.list']);
  } else if (role === 'SUPERVISOR') {
    return allPermissionsForScope('recording.own')
      .concat(allPermissionsForScope('recording.group'));
  } else {
    return allPermissionsForScope('recording.own');
  }
}

export function fullRecordingingPermissionsForRoleOnly(role: APP_USER_RECORDING_ROLE) {
  if ( role === 'OWNER') {
    return allPermissionsForScope('recording.all');
  } else if (role === 'ADMINISTRATOR') {
      return ['recording.all.list'];
  } else if (role === 'SUPERVISOR') {
    return allPermissionsForScope('recording.group');
  } else {
    return allPermissionsForScope('recording.own');
  }
}

export function mergePermissionsForRole(permissions: string[], role: APP_USER_RECORDING_ROLE) {
  const miniScopes = RECORDING_ROLE_LEVEL_MAPPING[role]; // ['own', 'group'] for example
  const fullListScopes = miniScopes.map(t => `recording.${t}.list`); // ['recording.own.list', 'recording.group.list'] for example

  // If they have view permissions, they should also have view confidential permission (implied)
  const viewOwnConfidentialList =
    permissions.find(t => t === 'recording.own.view')
      ? ['recording.own.confidential'] : [];

  // now merge those into the permissions if they don't already exist
  return permissions
    .concat(fullListScopes.filter(t => !permissions.includes(t)))
    .concat(viewOwnConfidentialList.filter(t => !permissions.includes(t)));
}

export function permissionSummaryForRole(permissions: string[], role: APP_USER_RECORDING_ROLE) {
  const hasAllForRole = hasFullRecordingPermissionsForRole(permissions, role);
  return hasAllForRole ? 'Full' : 'Partial';
}

export function recordingOwnerPermissions(permissions: string[]) {
  return permissions.filter(t => t.startsWith('recording.own.'))
    .map(t => t.substring('recording.own.'.length));
}

export function recordingSupervisorPermissions(permissions: string[]) {
  return permissions.filter(t => t.startsWith('recording.group.'))
    .map(t => t.substring('recording.group.'.length));
}

export function recordingAdminPermissions(permissions: string[]) {
  return permissions.filter(t => t.startsWith('recording.all.'))
    .map(t => t.substring('recording.all.'.length));
}

export function isRecordingSupervisor(permissions: string[]) {
  return hasAnyRecordingPermissionsForScope(permissions, 'recording.group');
}

export function isRecordingAdmin(permissions: string[]) {
  return permissions.includes(RECORDING_PERMISSIONS.List_All);
}

export function hasAllRecordingPermissionsForScope(permissions: string[], scope: RecordingScope) {
  return allPermissionsForScope(scope).every(t => permissions.includes(t));
}

export function hasAnyRecordingPermissionsForScope(permissions: string[], scope: RecordingScope) {
  return allPermissionsForScope(scope).some(t => permissions.includes(t));
}

export function allPermissionsForScope(scope: RecordingScope) {
  return RECORDING_PEMISSION_ACTIONS.map(t => `${scope}.${t}`);
}

export function getAllPossibleRecordingPermissions() {
  return allPermissionsForScope('recording.own')
    .concat(allPermissionsForScope('recording.group'))
    .concat(allPermissionsForScope('recording.all'));
}


export function recordingPermissionsToFormData(permissions: string[]) {
  return ['ownPermissions', 'groupPermissions', 'adminPermissions']
    .reduce((acc, category) => {
      const scope = category === 'ownPermissions' ? 'recording.own'
        : category === 'groupPermissions' ? 'recording.group'
        : 'recording.all';

      return {
        ...acc,
        [category]: {
          data: allPermissionsForScope(scope).reduce((acc, permission) => {
            return {
              ...acc,
              [permission]: permissions.includes(permission)
            };
          }, {})
        }
      };
    }, {});
}

export function recordingPermissionsFromFormData(data: any) {
  return ['ownPermissions', 'groupPermissions', 'adminPermissions']
    .flatMap(key => Object.entries(data[key]?.data || {})) // Flatten the entries from each permission object
    .filter(([, value]) => value === true) // Keep only the ones that are true
    .map(([permission]) => permission);
}

